import { SelectionModel } from "@angular/cdk/collections";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Sort, SortDirection } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  DialogButton,
} from "@cotecna/dialogs";
import { InfiniteScrollDirective } from "@cotecna/directives";
import {
  ColumnDataType,
  ComplexString,
  TableAction,
  TableColumn,
} from "@cotecna/tables";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { StorageService } from "src/app/session/services/storage.service";
import { FeatureFlagsService } from "src/app/shared/services/feature-flags/feature-flags.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import {
  APPROVED_TIME_MANAGEMENT,
  CLONE_INSPECTION_ACTION_NAME,
  COMDIV,
  FALCON,
  InspectionListState,
  InspectionTimeManagementState,
  PENDING_TIME_MANAGEMENT,
  RETURNED_TIME_MANAGEMENT,
  SHOW_DOCUMENTS_ACTION_NAME,
  SORT_DIRECTION_KEY,
  SORT_ORDER_KEY,
  SUBMITTED_TIME_MANAGEMENT,
} from "../constants/constants";
import { CloneInspectionData } from "../dialogs/clone-inspection/clone-inspection-dialog/clone-inspection-data";
import { CloneInspectionDialogComponent } from "../dialogs/clone-inspection/clone-inspection-dialog/clone-inspection-dialog.component";
import { LoadingComponent } from "../dialogs/loading/loading.component";
import { InspectionInMainPage } from "../model/inspection";
import { InspectionState } from "../model/inspection-state";
import { InspectionsListConfig } from "../permissions-setter/permissions-interface";
import { InspectionFilterService } from "../services/inspection-filter.service";
import { InspectionListService } from "../services/inspection-list.service";
import { PermissionsService } from "../services/permissions.service";
import { FilterForm } from "./filters/filter-form-interface";
import { FiltersComponent } from "./filters/filters.component";

interface RowData {
  inspectionId: string;
  status: string;
  origin: string;
  externalId: string;
  client: ComplexString;
  inspectionReference: string;
  inspectionDateTime: Date;
  creationDate: Date;
  closeDate?: Date;
  place: string;
  assignedTo: string[];
}
interface DefaultArrowStatus {
  columnName: string;
  direction: SortDirection;
}

const COLUMNS: TableColumn[] = [
  {
    dataProperty: "status",
    isSorting: false,
    label: "Status",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "origin",
    isSorting: false,
    label: "Origin",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "externalId",
    isSorting: true,
    label: "Id",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "client",
    isSorting: false,
    label: "Client",
    dataType: ColumnDataType.ComplexString,
    width: 0,
  },
  {
    dataProperty: "inspectionReference",
    isSorting: false,
    label: "Inspection Reference",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "preset",
    isSorting: false,
    label: "Inspection Type",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "place",
    isSorting: false,
    label: "Inspection Place",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "commodity",
    isSorting: false,
    label: "Commodity",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "transportName",
    isSorting: false,
    label: "Transport",
    dataType: ColumnDataType.String,
    width: 0,
  },
  {
    dataProperty: "assignedTo",
    isSorting: false,
    label: "Asg.",
    dataType: ColumnDataType.AvatarArray,
    width: 80,
  },
  {
    dataProperty: "inspectionDateTime",
    isSorting: true,
    label: "Inspection Date/Time",
    dataType: ColumnDataType.DateTime,
    width: 0,
  },
  {
    dataProperty: "creationDate",
    isSorting: true,
    label: "Creation Date",
    dataType: ColumnDataType.Date,
    width: 0,
  },
  {
    dataProperty: "closeDate",
    isSorting: true,
    label: "Closing Date",
    dataType: ColumnDataType.Date,
    width: 0,
  },
  {
    dataProperty: "timeManagementState",
    isSorting: false,
    label: "Timesheet Status",
    dataType: ColumnDataType.String,
    width: 0,
  },
];

const ACTIONS: TableAction[] = [
  {
    icon: "chevron_right",
    name: "view_inspection",
    tooltip: "View inspection",
  },
];

const PageSize: number = 50;

@Component({
  selector: "im-inspections",
  templateUrl: "./inspections.component.html",
  styleUrls: ["./inspections.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InspectionsComponent implements OnInit {
  @ViewChild(InfiniteScrollDirective)
  public infiniteScroll: InfiniteScrollDirective;

  @ViewChild(FiltersComponent)
  public filtersComponent: FiltersComponent;

  public hasLoadedAllInspections: boolean = false;

  public isSearchButtonDisabled: boolean = false;
  public isFilterOpen: boolean;
  public isStateActive: boolean = true;
  public isHideOriginFilter: boolean = true;
  public isClearAll: boolean = false;

  public oldfilterFormToSend: FilterForm;

  private permissions: InspectionsListConfig = {
    allowNewInspection: false,
  };

  public dataSource: MatTableDataSource<InspectionInMainPage>;
  private inspections: BehaviorSubject<InspectionInMainPage[]> =
    new BehaviorSubject<InspectionInMainPage[]>([]);

  @Output() public selectionChange: EventEmitter<InspectionInMainPage[]> =
    new EventEmitter<InspectionInMainPage[]>();
  @Input() public clearAllSelection: EventEmitter<InspectionInMainPage[]> =
    new EventEmitter<InspectionInMainPage[]>();

  public selection = new SelectionModel<InspectionInMainPage>(true, []);
  public PAGE_SIZE = PageSize;
  public currentPage: number = 0;
  public currentInspectionStatus: InspectionListState =
    InspectionListState.Active;

  private order: string = "inspectionDateTime";
  private descending: boolean = false;
  public defaultArrowStatus: DefaultArrowStatus = {
    columnName: this.order,
    direction: "asc",
  };

  public isLoading: boolean = true;
  public selectedVal: InspectionListState = InspectionListState.Active;

  public columns = COLUMNS;
  public dataChanges: Observable<RowData[]> = this.inspections.pipe(
    map((inspections) => this.toRowData(inspections)),
  );
  public actions = ACTIONS;
  private openInspectionFromClickOnRow: boolean = true;
  private filterFlag: boolean = false;
  private filterValues: FilterForm;
  public displayColumns = COLUMNS;

  public isAccountTypeManagementOnly: boolean = false;
  public isInspectionsOnlyAccount: boolean = false;
  public isInspectionsAndTimeManagementAccount: boolean = false;

  public constructor(
    public matDialog: MatDialog,
    private router: Router,
    private inspectionListService: InspectionListService,
    private dialog: MatDialog,
    private permissionsService: PermissionsService,
    private userRolePermissionsService: UserRolePermissionsService,
    private storageService: StorageService,
    private featureFlagsService: FeatureFlagsService,
    private inspectionFilterService: InspectionFilterService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(LOCALE_ID) public locale: string,
  ) {
    this.selectedVal = history.state?.activeTabState
      ? history.state.activeTabState
      : InspectionListState.Active;
    this.currentInspectionStatus = this.selectedVal;
  }

  public async ngOnInit() {
    await this.setPermissions();
    this.getAccountType();
    this.addActionsBasedOnAccountType();
    this.subscribeToInspectionChanges();
    this.selection.changed.subscribe(() => {
      this.selectionChange.emit(this.selection.selected);
    });
    this.applyColumnVisibilityChecks(this.selectedVal);
    this.isFilterOpen = this.inspectionFilterService.getFilterStatus();
    const filterValues = this.inspectionFilterService.getFilterForm();
    if (filterValues && this.hasNonEmptyValues(filterValues)) {
      await this.getFilteredInspectionList(filterValues);
    } else {
      await this.getInspections(this.selectedVal);
    }
  }

  public async addActionsBasedOnAccountType(): Promise<void> {
    if (
      !this.actions.some((a) => a.name == SHOW_DOCUMENTS_ACTION_NAME) &&
      !this.isAccountTypeManagementOnly
    ) {
      this.actions.splice(0, 0, {
        icon: "description",
        name: SHOW_DOCUMENTS_ACTION_NAME,
        tooltip: "Show documents",
      });
    }
    if (
      this.permissions.allowNewInspection &&
      !this.actions.some((a) => a.name == CLONE_INSPECTION_ACTION_NAME) &&
      !this.userRolePermissionsService.isInspector() &&
      !this.userRolePermissionsService.isServiceProvider() &&
      !this.isAccountTypeManagementOnly
    )
      this.actions.splice(1, 0, {
        icon: "content_copy",
        name: CLONE_INSPECTION_ACTION_NAME,
        tooltip: "Clone inspection",
      });
    if (
      this.permissions.allowNewInspection &&
      !this.actions.some((a) => a.name == "delete") &&
      !this.userRolePermissionsService.isInspector() &&
      !this.userRolePermissionsService.isServiceProvider()
    ) {
      const index = this.isAccountTypeManagementOnly ? 0 : 2;
      this.actions.splice(index, 0, {
        icon: "delete_outline",
        name: "delete",
        tooltip: "Delete",
      });
    }
  }

  private async getAccountType(): Promise<void> {
    this.isInspectionsOnlyAccount =
      this.userRolePermissionsService.isInspectionsOnlyAccount();
    this.isAccountTypeManagementOnly =
      this.userRolePermissionsService.isTimeManagementOnlyAccount();
    this.isInspectionsAndTimeManagementAccount =
      this.userRolePermissionsService.isInspectionsAndTimeManagementAccount();
  }

  public async setPermissions() {
    const permissions = await this.permissionsService.getPermissions();
    this.permissions = permissions.setInspectionsListConfig();
    this.changeDetectorRef.markForCheck();
  }

  public async onInspectionListStateChange(value: InspectionListState) {
    if (this.selectedVal == value) return;
    this.applyColumnVisibilityChecks(value);
    await this.updateInspectionList(value);
  }

  public setTableInteractions(): string {
    return this.isLoading ? "interactions-disabled" : null;
  }

  public async updateInspectionList(value: InspectionListState) {
    this.hasLoadedAllInspections = false;
    this.isLoading = true;
    this.selectedVal = value;
    this.currentPage = 0;
    this.inspections.next([]);
    this.infiniteScroll.resetMaxScrollOffset();
    await this.getInspections(value);
  }

  public disableActionIcon(row: any, action: TableAction): boolean {
    if (
      action.name === CLONE_INSPECTION_ACTION_NAME &&
      (row?.origin?.toLowerCase() === COMDIV ||
        row?.origin?.toLowerCase() === FALCON)
    ) {
      action.tooltip = "";
      return true;
    } else if (
      action.name === CLONE_INSPECTION_ACTION_NAME &&
      row?.origin?.toLowerCase() != COMDIV &&
      row?.origin?.toLowerCase() != FALCON
    ) {
      action.tooltip = "Clone inspection";
      return false;
    }
    return false;
  }

  private async getInspections(value: InspectionListState) {
    if (this.currentInspectionStatus !== value) {
      this.currentPage = 0;
      this.PAGE_SIZE = PageSize;
    }
    this.currentInspectionStatus = value;
    const filterValues = this.inspectionFilterService.filterForm;
    switch (value) {
      case InspectionListState.Active:
        filterValues.closeDate = "";
        if (
          this.inspectionFilterService.filterForm.status ==
          InspectionState.Closed
        )
          filterValues.status = "";
        if (filterValues && this.hasNonEmptyValues(filterValues)) {
          await this.getFilteredInspectionList(filterValues);
        } else {
          await this.getByAccount();
        }
        break;
      case InspectionListState.Closed:
        if (
          this.inspectionFilterService.filterForm.status !=
          InspectionState.Closed
        )
          filterValues.status = "";
        if (filterValues && this.hasNonEmptyValues(filterValues)) {
          await this.getFilteredInspectionList(filterValues);
        } else {
          await this.getClosedInspectionsByAccount();
        }
        break;
    }
  }

  public async getFilteredInspectionList(
    filterFormToSend: FilterForm,
  ): Promise<void> {
    if (
      filterFormToSend.client === "" &&
      filterFormToSend.dateFrom === "" &&
      filterFormToSend.dateTo === "" &&
      filterFormToSend.inspectionId === "" &&
      filterFormToSend.origin === "" &&
      filterFormToSend.status === "" &&
      filterFormToSend.type === "" &&
      filterFormToSend.creationDateFrom === "" &&
      filterFormToSend.creationDateTo === "" &&
      filterFormToSend.closeDate === "" &&
      filterFormToSend.inspectionReference === "" &&
      filterFormToSend.vesselName === "" &&
      filterFormToSend.timeManagementState === ""
    ) {
      this.filterFlag = false;
      this.updateInspectionList(this.selectedVal);
    } else {
      this.isLoading = true;
      this.disableSearchButton();
      this.filterValues = filterFormToSend;
      this.isLoading = true;
      const isActive = this.currentInspectionStatus == "active" ? true : false;
      if (
        JSON.stringify(this.oldfilterFormToSend) !==
        JSON.stringify(filterFormToSend)
      ) {
        this.inspections.next([]);
        this.currentPage = 0;
        this.hasLoadedAllInspections = false;
        this.infiniteScroll.resetMaxScrollOffset();
      }
      await this.retrieveSortingInformation();
      const inspections = await this.inspectionListService.getFiltered(
        this.currentPage,
        this.PAGE_SIZE,
        this.order,
        this.descending,
        filterFormToSend,
        isActive,
      );
      this.enableSearchButton();

      if (inspections) this.filterFlag = true;

      this.manageInspectionList(inspections);
      this.isLoading = false;
    }
    this.oldfilterFormToSend = { ...filterFormToSend };
  }

  public async getByAccount() {
    await this.retrieveSortingInformation();
    const inspections =
      this.userRolePermissionsService.isInspector() ||
      this.userRolePermissionsService.isServiceProviderInspector()
        ? await this.inspectionListService.getActiveNominatedInspections(
            this.currentPage,
            this.PAGE_SIZE,
            this.order,
            this.descending,
          )
        : this.userRolePermissionsService.isServiceProviderCoordinator()
          ? await this.inspectionListService
              .getServiceProviderCoordinatorActiveInspections(
                this.currentPage,
                this.PAGE_SIZE,
                this.order,
                this.descending,
              )
              .toPromise()
          : this.permissions.externalUser
            ? await this.inspectionListService
                .getByClient(
                  this.currentPage,
                  this.PAGE_SIZE,
                  this.order,
                  this.descending,
                )
                .toPromise()
            : await this.inspectionListService
                .getByAccount(
                  this.currentPage,
                  this.PAGE_SIZE,
                  this.order,
                  this.descending,
                )
                .toPromise();

    this.manageInspectionList(inspections);
  }

  public async retrieveSortingInformation() {
    const order = await this.storageService.get(SORT_ORDER_KEY);
    if (order) {
      if (
        this.selectedVal == InspectionListState.Active &&
        order === "closeDate"
      ) {
        this.order = "inspectionDateTime";
      } else {
        this.order = order;
      }
    } else {
      this.order =
        this.selectedVal == InspectionListState.Active
          ? "inspectionDateTime"
          : "closeDate";
    }
    this.defaultArrowStatus.columnName = this.order;

    const direction = await this.storageService.get(SORT_DIRECTION_KEY);
    if (direction) {
      this.descending = direction === "desc";
      this.defaultArrowStatus.direction = direction;
    }
  }

  public async getClosedInspectionsByAccount() {
    await this.retrieveSortingInformation();
    const inspections =
      this.userRolePermissionsService.isInspector() ||
      this.userRolePermissionsService.isServiceProviderInspector()
        ? await this.inspectionListService.getClosedNominatedInspections(
            this.currentPage,
            this.PAGE_SIZE,
            this.order,
            this.descending,
          )
        : this.userRolePermissionsService.isServiceProviderCoordinator()
          ? await this.inspectionListService
              .getServiceProviderCoordinatorClosedInspections(
                this.currentPage,
                this.PAGE_SIZE,
                this.order,
                this.descending,
              )
              .toPromise()
          : this.permissions.externalUser
            ? await this.inspectionListService
                .getClosedInspectionsByClient(
                  this.currentPage,
                  this.PAGE_SIZE,
                  this.order,
                  this.descending,
                )
                .toPromise()
            : await this.inspectionListService
                .getClosedInspections(
                  this.currentPage,
                  this.PAGE_SIZE,
                  this.order,
                  this.descending,
                )
                .toPromise();

    this.manageInspectionList(inspections);
  }

  public showFilters(): boolean {
    return this.featureFlagsService.showInspectionsFilter();
  }

  private manageInspectionList(
    inspections: InspectionInMainPage[] | null | undefined,
  ): void {
    const inspectionList = inspections ?? [];
    this.hasLoadedAllInspections = inspectionList.length < this.PAGE_SIZE;
    this.isLoading = false;
    this.inspections.next(this.avoidDuplicates(inspectionList));
  }

  public async getNextPageByAccount() {
    if (!this.hasLoadedAllInspections) {
      this.isLoading = true;
      this.currentPage++;
      await this.getInspections(this.selectedVal);
    }
  }

  private avoidDuplicates(
    inspections: InspectionInMainPage[],
  ): InspectionInMainPage[] {
    const allInspections = this.isClearAll
      ? inspections
      : this.inspections.value.concat(inspections);
    const uniqueValues = allInspections.filter(
      (
        inspection: InspectionInMainPage,
        index: number,
        self: InspectionInMainPage[],
      ) => {
        return index == self.findIndex((ins) => ins.id === inspection.id);
      },
    );
    this.isClearAll = false;
    return uniqueValues;
  }

  private subscribeToInspectionChanges() {
    this.inspections.subscribe((inspections) => {
      this.dataSource = new MatTableDataSource<InspectionInMainPage>(
        inspections,
      );
    });
  }

  private toRowData(inspections: InspectionInMainPage[]) {
    const toRowData: (_: InspectionInMainPage) => RowData = (inspection) => ({
      inspectionId: inspection.id,
      status: this.getInspectionState(inspection.state),
      origin: inspection.originApplicationName,
      externalId: inspection.externalId,
      client:
        inspection.externalCoordinators?.length > 0
          ? {
              text: inspection.client,
              color: "#FF9458",
              icon: "/assets/images/external-client-icon.svg",
            }
          : {
              text: inspection.client,
            },
      inspectionReference: inspection.inspectionReference,
      preset: inspection.presetName,
      place: inspection.place,
      inspectionDateTime: inspection.date[0],
      creationDate: inspection.creationDate,
      closeDate: inspection.closeDate,
      timeManagementState: this.getTimeManagementLabel(
        inspection.timeManagementState,
      ),
      commodity: inspection.commodity,
      transportName: inspection.transportName,
      assignedTo: inspection.inspectors,
    });

    return inspections.map(toRowData);
  }

  public performAction(event) {
    if (!event.action) {
      return;
    }

    this.openInspectionFromClickOnRow = false;
    const rowData = event.data as RowData;
    const inspection = this.inspections.value.find(
      (i) => i.id === rowData.inspectionId,
    );

    switch (event.action.name) {
      case "show_documents":
        this.showDocuments(inspection);
        break;
      case "view_inspection":
        this.view(inspection);
        break;
      case "clone_inspection":
        this.cloneInspection(inspection);
        break;
      case "delete":
        this.deleteInspection(inspection);
        break;
    }
  }

  public onClickRow(result) {
    if (result.row && this.openInspectionFromClickOnRow) {
      const rowData = result.row as RowData;
      const inspectionToView = this.inspections.value.find(
        (inspection) => inspection.id === rowData.inspectionId,
      );
      this.view(inspectionToView);
    }
  }

  public addInspection() {
    this.router.navigate(["new-inspection"]);
  }

  public displayNewInspectionButton(): boolean {
    return (
      this.permissions.allowNewInspection &&
      !this.userRolePermissionsService.isInspector() &&
      !this.userRolePermissionsService.isServiceProvider()
    );
  }

  public inspectionIsClosed(inspection: InspectionInMainPage) {
    return inspection.state === InspectionState.Closed;
  }

  public view(inspection: InspectionInMainPage) {
    if (inspection.state == InspectionState.New)
      this.router.navigate([`new-inspection/${inspection.id}`]).then(() => {
        this.openInspectionFromClickOnRow = true;
      });
    else
      this.router
        .navigate([`details/${inspection.id}`], {
          state: { activeTabState: this.selectedVal },
        })
        .then(() => {
          this.openInspectionFromClickOnRow = true;
        });
  }

  private showDocuments(inspection: InspectionInMainPage) {
    if (inspection.state != InspectionState.New) {
      this.router
        .navigate([`details/${inspection.id}/documents`])
        .then(() => (this.openInspectionFromClickOnRow = true));
    } else this.view(inspection);
  }

  private async cloneInspection(inspection: InspectionInMainPage) {
    let loadingDialog: MatDialogRef<any> = null;
    try {
      const cloneDialog = this.openCloneInspectionDialog(inspection);
      const cloneInspectionData: CloneInspectionData = await cloneDialog
        .afterClosed()
        .toPromise();
      if (cloneInspectionData) {
        loadingDialog = this.openLoadingDialog("Cloning");
        await this.inspectionListService.cloneInspection(cloneInspectionData);
        await this.loadInspections();
      }
    } finally {
      this.openInspectionFromClickOnRow = true;
      loadingDialog?.close();
    }
  }

  private async deleteInspection(inspection: InspectionInMainPage) {
    let loadingDialog: MatDialogRef<any> = null;
    try {
      const deleteDialog = this.openDeleteInspectionDialog(
        inspection?.externalId,
      );
      const result = await deleteDialog.afterClosed().toPromise();
      if (result) {
        loadingDialog = this.openLoadingDialog("Deleting");
        await this.inspectionListService.deleteInspection(inspection.id);
        await this.loadInspections();
      }
    } finally {
      this.openInspectionFromClickOnRow = true;
      loadingDialog?.close();
    }
  }

  private async loadInspections() {
    this.hasLoadedAllInspections = false;
    this.isLoading = true;
    this.PAGE_SIZE =
      this.currentPage === 0
        ? this.PAGE_SIZE
        : (this.currentPage + 1) * this.PAGE_SIZE;
    this.currentPage = 0;
    this.inspections.next([]);
    this.subscribeToInspectionChanges();
    const filterValues = this.inspectionFilterService.filterForm;
    if (filterValues && this.hasNonEmptyValues(filterValues)) {
      await this.getFilteredInspectionList(filterValues);
    } else {
      await this.getInspections(this.selectedVal);
    }
  }

  public getInspectionState(status: InspectionState): string {
    return InspectionState.getInspectionState(status);
  }

  public getTimeManagementLabel(status: InspectionTimeManagementState): string {
    return InspectionState.getInspectionTimesheetState(status);
  }

  public async onSortChange(sort: Sort) {
    this.infiniteScroll.resetMaxScrollOffset();
    this.order = sort.active;
    this.storageService.set(SORT_ORDER_KEY, sort.active);
    this.descending = sort.direction === "desc" ? true : false;
    this.storageService.set(SORT_DIRECTION_KEY, sort.direction);
    const filterValues = this.inspectionFilterService.filterForm;
    if (filterValues && this.hasNonEmptyValues(filterValues)) {
      this.inspections.next([]);
      await this.getFilteredInspectionList(filterValues);
    } else {
      this.updateInspectionList(this.selectedVal);
    }
  }

  public async onFilterClick(): Promise<void> {
    const table = document.querySelector(".infiniteScroll") as HTMLElement;
    if (!this.isFilterOpen) {
      this.isFilterOpen = true;
      table.style.maxHeight = "calc(88vh - 250px)";
    } else {
      this.isFilterOpen = false;
      table.style.maxHeight = "calc(100vh - 250px)";
      this.filterFlag = false;
      this.isLoading = true;
      await this.getInspections(this.selectedVal);
    }
    this.inspectionFilterService.setFilterStatus(this.isFilterOpen);
  }

  private openLoadingDialog(message: string): MatDialogRef<LoadingComponent> {
    return this.dialog.open(LoadingComponent, {
      data: <LoadingComponent>{
        loadingText: message,
      },
      panelClass: "dialog",
    });
  }

  private openDeleteInspectionDialog(
    inspectionName,
  ): MatDialogRef<ConfirmDialogComponent> {
    return this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: <ConfirmDialogData>{
        cancelButton: <DialogButton>{
          text: "CANCEL",
        },
        icon: "delete_outlined",
        okButton: <DialogButton>{
          text: "REMOVE",
          color: "primary",
        },
        title: `Remove Inspection ${inspectionName}?`,
        message:
          "Are you sure that you want to remove this inspection? Any information already inserted will be lost and the job will disappear from the nominated inspectors list. It is not possible to revert this change once done.",
      },
    });
  }

  private openCloneInspectionDialog(
    inspectionToClone: InspectionInMainPage,
  ): MatDialogRef<CloneInspectionDialogComponent> {
    return this.dialog.open(CloneInspectionDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        inspection: inspectionToClone,
      },
      panelClass: "clone-inspection-dialog",
    });
  }
  private enableSearchButton(): void {
    this.isSearchButtonDisabled = false;
  }

  private disableSearchButton(): void {
    this.isSearchButtonDisabled = true;
  }

  public onEnterKey(): void {
    this.filtersComponent.onSearchClick();
  }

  public async getInitialInspectionList(): Promise<void> {
    this.isClearAll = true;
    this.oldfilterFormToSend = this.inspectionFilterService.getEmptyFilter();
    await this.getInspections(this.selectedVal);
  }

  private applyColumnVisibilityChecks(
    inspectionListState: InspectionListState,
  ): void {
    this.displayColumns = this.columns;
    this.showHideCloseDate(inspectionListState);
    this.showHideOriginColumn();
    this.showHidePlaceColumn();
    this.showHideTimesheetStatusColumn();
    this.showHideInspectionReferenceColumn();
    this.showHideCommodityColumn();
    this.showHideTransportColumn();
    this.showHideAssignedToColumn();
  }

  private showHideOriginColumn(): void {
    if (
      this.userRolePermissionsService.isServiceProvider() ||
      this.isAccountTypeManagementOnly
    ) {
      this.displayColumns = this.displayColumns.filter(
        (item) => item.dataProperty != "origin",
      );
      this.isHideOriginFilter = false;
    }
  }

  public showHidePlaceColumn(): void {
    if (
      this.isInspectionsOnlyAccount ||
      this.isInspectionsAndTimeManagementAccount ||
      this.userRolePermissionsService.isInspector()
    ) {
      this.displayColumns = this.displayColumns.filter(
        (item) => item.dataProperty != "place",
      );
    }
  }

  public showHideTimesheetStatusColumn(): void {
    if (this.isInspectionsOnlyAccount) {
      this.displayColumns = this.displayColumns.filter(
        (item) => item.dataProperty != "timeManagementState",
      );
    }
  }

  public showHideInspectionReferenceColumn(): void {
    if (
      !this.userRolePermissionsService.isInspector() &&
      this.isAccountTypeManagementOnly
    ) {
      this.displayColumns = this.displayColumns.filter(
        (item) => item.dataProperty != "inspectionReference",
      );
    }
  }

  private showHideCommodityColumn(): void {
    if (!this.isAccountTypeManagementOnly) {
      this.displayColumns = this.displayColumns.filter(
        (column) => column.dataProperty != "commodity",
      );
    }
  }

  private showHideTransportColumn(): void {
    if (!this.isAccountTypeManagementOnly) {
      this.displayColumns = this.displayColumns.filter(
        (column) => column.dataProperty != "transportName",
      );
    }
  }

  private showHideAssignedToColumn(): void {
    if (
      !this.userRolePermissionsService.isCoordinatorOrServiceProviderCoordinator()
    ) {
      this.displayColumns = this.displayColumns.filter(
        (column) => column.dataProperty != "AssignedTo",
      );
    }
  }

  private showHideCloseDate(value: InspectionListState): void {
    this.order =
      value === InspectionListState.Closed ? "closeDate" : "inspectionDateTime";
    if (value === "active") {
      this.displayColumns = this.displayColumns.filter(
        (item) => item.dataProperty != "closeDate",
      );
      this.isStateActive = true;
    } else {
      this.isStateActive = false;
    }
  }

  private hasNonEmptyValues(filterValues: FilterForm): boolean {
    return (
      Object.values(filterValues).find((value) => value !== "") !== undefined
    );
  }

  public changeCellTextStyle(row: any, column: TableColumn): any {
    if (column.dataProperty === "timeManagementState") {
      if (row.timeManagementState === PENDING_TIME_MANAGEMENT) {
        return {
          "background-color": "#D4D4D4",
          padding: "4px 12px",
          "border-radius": "24px",
          color: "black",
        };
      } else if (row.timeManagementState === SUBMITTED_TIME_MANAGEMENT) {
        return {
          "background-color": "#CAE4FE",
          padding: "4px 12px",
          "border-radius": "24px",
          color: "black",
        };
      } else if (row.timeManagementState === APPROVED_TIME_MANAGEMENT) {
        return {
          "background-color": "#8AD6B3",
          padding: "4px 12px",
          "border-radius": "24px",
          color: "black",
        };
      } else if (row.timeManagementState === RETURNED_TIME_MANAGEMENT) {
        return {
          "background-color": "#FCA5A5",
          padding: "4px 12px",
          "border-radius": "24px",
          color: "black",
        };
      }
    }
    return {};
  }
}
